import { gql, useMutation, useQuery } from '@apollo/client'
import { FetchResult } from '@apollo/client'
import { useEffect } from 'react'

import { currentArtistVar } from '@/clients/utoniq-core'
import {
  ArtistBottomTabFragmentDoc,
  ArtistGachaDetailFragmentDoc,
  ArtistLiveStream,
  ArtistLiveStreamQuery,
  ArtistLiveStreamQueryVariables,
  CurrentUserFragmentDoc,
  EscottMasterInput,
  GachaDetailForGuestQuery,
  GachaDetailForGuestQueryVariables,
  GachaDetailQuery,
  GachaDetailQueryVariables,
  GachaRollType,
  GachasDetailConnectionFragmentDoc,
  GachaTicketConnectionFragmentDoc,
  Maybe,
  PaymentMethod,
  PrepaidPointsPaymentSourceFragmentDoc,
  PurchasableGachaTicket,
  PurchasablePrepaidPointConnectionFragmentDoc,
  PurchasablePrepaidPointDetailFragment,
  PurchaseGachaTicketByPointInBulkMutation,
  PurchaseGachaTicketByPointInBulkMutationVariables,
  PurchaseGachaTicketByPointMutation,
  PurchaseGachaTicketByPointMutationVariables,
  RollGachaMutation,
  RollGachaMutationVariables,
  UserGachaTicketConnectionFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { usePointPurchase } from '@/components/pages/PrepaidPointCharge/viewModel'
import { config } from '@/config'
import { useCurrentUser, useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { getPlatform } from '@/core/native/app'

import { State } from './template'

export const useViewModel = makeViewModel<
  Pick<
    State,
    | 'user'
    | 'liveStreamId'
    | 'loading'
    | 'artist'
    | 'gachas'
    | 'gachaId'
    | 'gachaTicketPurchasing'
    | 'prepaidPointBalance'
    | 'ticket'
    | 'artistId'
    | 'gachaTicket'
    | 'purchasablePrepaidPoints'
    | 'pointChargeLoading'
    | 'paymentSource'
    | 'enablePointCarrierPayment'
    | 'isArtist'
    | 'memberArtists'
    | 'followArtists'
  >,
  {
    onRollGacha: (
      count: number,
      gachaId: string,
      rollType: GachaRollType
    ) => Promise<FetchResult<RollGachaMutation>>
    onPurchaseGachaTicket: (
      purchasableGachaTicket: Omit<PurchasableGachaTicket, 'id'>
    ) => Promise<
      FetchResult<
        | PurchaseGachaTicketByPointMutation
        | PurchaseGachaTicketByPointInBulkMutation
      >
    >
    onPurchaseCharge: (
      plan: PurchasablePrepaidPointDetailFragment,
      paymentMethod: Maybe<PaymentMethod>,
      escottInput: EscottMasterInput
    ) => Promise<void>
  }
>(() => {
  const { user: currentUser, userClaims } = useCurrentUser()
  const { router } = useRouter<{
    artistId: string
    gachaId?: string
  }>()
  const artistId = router.query.artistId ?? config.artistId
  const platform = getPlatform()
  const {
    purchaseByIosAndAndroid,
    purchaseByWeb,
    completeNativePurchase,
    setCompleteNativePurchase,
    chargeLoading,
  } = usePointPurchase(currentUser?.uid, platform, 'gachaDetail')

  const { data, loading } = useQuery<
    GachaDetailQuery,
    GachaDetailQueryVariables
  >(QUERY, {
    variables: {
      gachasArtistId: artistId,
      gachasPageInput: { limit: 20 },
      gachaTicketId: `organization:${config.organizationId}`,
      platform,
    },
    skip: !currentUser?.isSigned,
    notifyOnNetworkStatusChange: true,
    pollInterval: completeNativePurchase ? 2000 : 0,
    onCompleted(data) {
      if (data?.artist) {
        currentArtistVar(data.artist)
      }
    },
  })
  const isNextFetch = !!data

  const { data: dataForGuest, loading: loadingForGuest } = useQuery<
    GachaDetailForGuestQuery,
    GachaDetailForGuestQueryVariables
  >(QUERY_FOR_GUEST, {
    variables: {
      gachasArtistId: artistId,
      gachasPageInput: { limit: 20 },
      gachaTicketId: `organization:${config.organizationId}`,
    },
    skip: !!currentUser?.isSigned,
    notifyOnNetworkStatusChange: true,
    onCompleted(data) {
      if (data?.artist) {
        currentArtistVar(data.artist)
      }
    },
  })
  const isNextFetchForGuest = !!dataForGuest

  /** 決済後、prepaidPointBalanceが更新されたらpollingを停止する処理 */
  useEffect(() => {
    setCompleteNativePurchase(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.prepaidPointBalance])

  /**
   * @description
   * GraphqlCDNのキャッシュ対策のためクエリartist.currentLiveのクエリをgachaDetailとは別で実行
   * https://app.clickup.com/t/908127/UT-8880
   */
  const { data: dataLiveStream } = useQuery<
    ArtistLiveStreamQuery,
    ArtistLiveStreamQueryVariables
  >(ArtistLiveStream, {
    variables: { artistId },
    skip: !config.modules.liveStream,
  })

  const [rollGacha] = useMutation<
    RollGachaMutation,
    RollGachaMutationVariables
  >(MUTATION)

  const [
    purchaseGachaTicketByPoint,
    { loading: gachaTicketByPointPurchasing },
  ] = useMutation<
    PurchaseGachaTicketByPointMutation,
    PurchaseGachaTicketByPointMutationVariables
  >(PURCHASE_GACHA_TICKET_BY_POINT)
  const [
    purchaseGachaTicketByPointInBulk,
    { loading: gachaTicketByPointInBulkPurchasing },
  ] = useMutation<
    PurchaseGachaTicketByPointInBulkMutation,
    PurchaseGachaTicketByPointInBulkMutationVariables
  >(PURCHASE_GACHA_TICKET_BY_POINT_IN_BULK)

  return {
    state: {
      // 共通UI
      user: data?.user ?? null,
      isArtist: !!userClaims?.orgs?.[config.organizationId]?.artist,
      liveStreamId: dataLiveStream?.artist?.currentLive?.id,
      followArtists: data?.user?.organizationFollowees ?? null,
      memberArtists: data?.user?.organizationPatrons ?? null,
      // GachaDetailページのUI
      loading:
        (loading && !isNextFetch) || (loadingForGuest && !isNextFetchForGuest),
      artist: data?.artist ?? dataForGuest?.artist ?? null,
      artistId,
      gachas: data?.gachas ?? dataForGuest?.gachas ?? null,
      gachaId: router.query.gachaId ?? '',
      gachaTicketPurchasing:
        gachaTicketByPointPurchasing || gachaTicketByPointInBulkPurchasing,
      prepaidPointBalance: data?.prepaidPointBalance ?? null,
      ticket:
        (loading && !data?.user?.gachaTicket
          ? ''
          : data?.user?.gachaTicket?.available) ?? 0,
      gachaTicket: data?.gachaTicket ?? dataForGuest?.gachaTicket ?? null,
      purchasablePrepaidPoints: data?.purchasablePrepaidPoints ?? null,
      pointChargeLoading: chargeLoading,
      paymentSource: data?.paymentSource ?? null,
      /** アプリ毎に設定されているポイント購入のキャリア決済が有効かどうか */
      enablePointCarrierPayment:
        !!data?.organization?.modules?.pointCarrierPayment,
    },
    actions: {
      async onRollGacha(
        count: number,
        gachaId: string,
        rollType: GachaRollType
      ) {
        return rollGacha({
          variables: {
            rollGachaGachaId: gachaId,
            rollGachaInputs: {
              count,
              gachaTicketId: `organization:${config.organizationId}`,
              rollType,
            },
          },
        })
      },
      async onPurchaseGachaTicket(
        purchasableGachaTicket: Omit<PurchasableGachaTicket, 'id'>
      ) {
        return purchasableGachaTicket.count === 1
          ? purchaseGachaTicketByPoint({
              variables: {
                gachaTicketId: `organization:${config.organizationId}`,
                inputs: {
                  count: purchasableGachaTicket.count,
                  point: purchasableGachaTicket.prepaidPoints ?? 0,
                },
              },
            })
          : purchaseGachaTicketByPointInBulk({
              variables: {
                gachaTicketId: `organization:${config.organizationId}`,
                inputs: { point: purchasableGachaTicket.prepaidPoints ?? 0 },
              },
            })
      },
      onPurchaseCharge:
        platform === 'web' ? purchaseByWeb : purchaseByIosAndAndroid,
    },
  }
})

const QUERY = gql`
  query GachaDetail(
    $gachasArtistId: ID
    $gachasPageInput: PageInputDate
    $gachaTicketId: ID!
    $platform: String!
    $purchasablePrepaidPointPageInput: PageInput
  ) {
    gachas(artistId: $gachasArtistId, pageInput: $gachasPageInput) {
      ...GachasDetailConnection
    }
    user {
      ...CurrentUser
      gachaTicket(id: $gachaTicketId) {
        ...UserGachaTicketConnection
      }
    }
    gachaTicket(id: $gachaTicketId) {
      ...GachaTicketConnection
    }
    prepaidPointBalance {
      prepaidPointId
      balance
      formattedPrepaidPointBarance @client
    }
    artist(id: $gachasArtistId) {
      ...ArtistBottomTab
      ...ArtistGachaDetail
    }
    purchasablePrepaidPoints(
      platform: $platform
      pageInput: $purchasablePrepaidPointPageInput
    ) {
      ...PurchasablePrepaidPointConnection
    }
    paymentSource {
      ...PrepaidPointsPaymentSource
    }
    organization {
      id
      modules {
        pointCarrierPayment
      }
    }
  }
  ${ArtistBottomTabFragmentDoc}
  ${GachasDetailConnectionFragmentDoc}
  ${GachaTicketConnectionFragmentDoc}
  ${UserGachaTicketConnectionFragmentDoc}
  ${CurrentUserFragmentDoc}
  ${PurchasablePrepaidPointConnectionFragmentDoc}
  ${PrepaidPointsPaymentSourceFragmentDoc}
  ${CurrentUserFragmentDoc}
  ${ArtistGachaDetailFragmentDoc}
`

const QUERY_FOR_GUEST = gql`
  query GachaDetailForGuest(
    $gachasArtistId: ID
    $gachasPageInput: PageInputDate
    $gachaTicketId: ID!
  ) {
    gachas(artistId: $gachasArtistId, pageInput: $gachasPageInput) {
      ...GachasDetailConnection
    }
    gachaTicket(id: $gachaTicketId) {
      ...GachaTicketConnection
    }
    artist(id: $gachasArtistId) {
      ...ArtistBottomTab
      ...ArtistGachaDetail
    }
  }
  ${ArtistBottomTabFragmentDoc}
  ${GachasDetailConnectionFragmentDoc}
  ${GachaTicketConnectionFragmentDoc}
  ${ArtistGachaDetailFragmentDoc}
`

const MUTATION = gql`
  mutation RollGacha(
    $rollGachaGachaId: ID!
    $rollGachaInputs: RollGachaInput!
  ) {
    rollGacha(gachaId: $rollGachaGachaId, inputs: $rollGachaInputs) {
      gachaOrder {
        id
        gachaId
        rewardItems {
          count
        }
        rewardAnimation {
          uri
          mediumUri
          smallUri
        }
      }
    }
  }
`

const PURCHASE_GACHA_TICKET_BY_POINT = gql`
  mutation PurchaseGachaTicketByPoint(
    $gachaTicketId: ID!
    $inputs: PurchaseGachaTicketPointInput!
  ) {
    purchaseGachaTicketByPoint(gachaTicketId: $gachaTicketId, inputs: $inputs) {
      gachaTicketId
      count
    }
  }
`

const PURCHASE_GACHA_TICKET_BY_POINT_IN_BULK = gql`
  mutation PurchaseGachaTicketByPointInBulk(
    $gachaTicketId: ID!
    $inputs: PurchaseGachaTicketPointInBulkInput!
  ) {
    purchaseGachaTicketByPointInBulk(
      gachaTicketId: $gachaTicketId
      inputs: $inputs
    ) {
      gachaTicketId
      count
    }
  }
`
